import type { QueryClient } from '@tanstack/react-query'
import type { PersistedClient, Persister } from '@tanstack/react-query-persist-client'

/**
 * The release version is the persisted-cache compatibility boundary. Query keys are otherwise
 * the only compatibility boundary: sanitizePersistedClient verifies structured-clone compatibility,
 * never the domain shape that new code will receive. Tying the buster to the release version means
 * each release invalidates the persisted cache automatically, without relying on a developer to
 * notice an incompatible payload change and bump a separate schema constant.
 *
 * The accepted cost is that every deploy with a new release version silently wipes every web
 * user's persisted query cache, and recovery is NOT automatic. Going back online only restores what
 * the root loader fetches: participations, the current user, and the current stage's scaffolding
 * (rootLoader.ts). The bulk per-stage offline data — issues with their first page of comments and
 * events, floor-plan SVGs, attachments — comes back only when someone presses Download per stage in
 * the offline-cache settings screen (useStagePrefetch via StageCacheRow). So a user who updates and
 * then goes offline is left with an empty query cache for every stage they had downloaded, until
 * they re-download each one deliberately.
 *
 * That per-stage download runs the SAME three phases, not a reduced path: useStagePrefetch hands a
 * single-stage array to the shared prefetchIssues engine (prefetchSingleStage), which still reaches
 * floor-plan SVGs and attachments. What has no production caller is usePrefetchIssues — the hook
 * that used to autostart that engine for ALL stages at app start, retired by c78d68378 — even though
 * some docs and comments still describe it as mounted at the app root. So the pipeline is alive and
 * only its automatic multi-stage trigger is gone; do not read "no caller" as "no prefetch".
 * Attachment FILES cached in Dexie survive the wipe (separate store, own TTL), but the issues
 * referencing them do not.
 *
 * __APP_VERSION__ comes from app/package.json and is bumped by the release:* scripts, not by every
 * deploy. Multiple staging deploys can therefore share a buster. If a persisted payload shape
 * changes, bump the package version so the incompatible cache is invalidated.
 */
export const PERSISTED_CACHE_BUSTER = __APP_VERSION__

export type PersistedCacheRestoreResult = {
  restored: number
  droppedExpired: number
  discarded: 'buster' | 'max-age' | 'no-timestamp' | null
  prunedSnapshot: boolean
  gatedSnapshot: PersistedClient | undefined
}

export async function restorePersistedQueryCache(options: {
  queryClient: QueryClient
  persister: Persister
  maxAge: number
  buster: string
  now?: () => number
  pausePersist: () => void
  resumePersist: () => Promise<void>
}): Promise<PersistedCacheRestoreResult> {
  const {
    queryClient,
    persister,
    maxAge,
    buster,
    now = Date.now,
    pausePersist,
    resumePersist,
  } = options
  const emptyResult = {
    restored: 0,
    droppedExpired: 0,
    discarded: null,
    prunedSnapshot: false,
    gatedSnapshot: undefined,
  } satisfies PersistedCacheRestoreResult

  const persisted = await persister.restoreClient()
  if (!persisted) return emptyResult

  // Storage operations are awaited and deliberately allowed to reject. A failed removal aborts
  // before an invalid snapshot can hydrate; initializeApp catches the error so startup continues.
  if (!persisted.timestamp) {
    await persister.removeClient()
    return { ...emptyResult, discarded: 'no-timestamp' }
  }

  if (persisted.buster !== buster) {
    await persister.removeClient()
    return { ...emptyResult, discarded: 'buster' }
  }

  // The strict operator comes from persistQueryClientRestore so both restore paths agree exactly
  // at the maxAge boundary.
  if (now() - persisted.timestamp > maxAge) {
    await persister.removeClient()
    return { ...emptyResult, discarded: 'max-age' }
  }

  const queries = persisted.clientState?.queries ?? []
  const keptQueries: Array<(typeof queries)[number]> = []
  let restored = 0
  let droppedExpired = 0

  pausePersist()
  try {
    for (const query of queries) {
      if (query.state.data === undefined) {
        keptQueries.push(query)
        continue
      }

      // The snapshot timestamp is rewritten on every persist, so the client-level check above
      // mainly detects an app that was not opened for maxAge. This per-query timestamp is the gate
      // that prevents old data from riding indefinitely inside newly timestamped snapshots.
      if (now() - (query.state.dataUpdatedAt ?? 0) > maxAge) {
        droppedExpired += 1
        continue
      }

      keptQueries.push(query)
      // updatedAt is load-bearing: without it, every cold start re-stamps cached data as fresh.
      // The entry could then outlive maxAge forever, while staleTime and gcTime measure from a lie.
      queryClient.setQueryData(query.queryKey, query.state.data, {
        updatedAt: query.state.dataUpdatedAt,
      })
      restored += 1
    }
  } finally {
    await resumePersist()
  }

  const gatedSnapshot: PersistedClient = {
    ...persisted,
    clientState: { ...persisted.clientState, queries: keptQueries },
  }

  let prunedSnapshot = false
  if (droppedExpired > 0) {
    // Keep this separate storage-hygiene write after resumePersist() so hydration's persistence
    // pause is closed first. resumePersist() would flush a buffered client, but this order is
    // simpler. The prune is belt-and-braces and best-effort: safeSet logs and resolves write
    // failures, and correctness no longer depends on it because the provider consumes the gated
    // snapshot below instead of re-reading storage.
    await persister.persistClient(gatedSnapshot)
    prunedSnapshot = true
  }

  return {
    restored,
    droppedExpired,
    discarded: null,
    prunedSnapshot,
    gatedSnapshot,
  }
}
